import { CashPlusSubscriptionsState } from "@modules/cashplus/+state/cashplus-subscriptions.state.service";
import { CashplusPortfolioFacade } from "@modules/cashplus/+state/cashplus-portfolio.facade";
import { CashPlusPortfolioState } from "@modules/cashplus/+state/cashplus-portfolio.state.service";
import { Injectable, inject } from "@angular/core";
import { combineLatest, Observable, EMPTY } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { AccountService } from "@modules/account/services/account.service";
import { FiatService } from "@main-services/currency";
import { Fiat, FiatWallet } from "@models";
import { CashPlusFacade } from "@modules/cashplus/+state/cashplus.facade";
import {
	FiatWalletsConfigurations,
	ICashPlusFiatWallet,
	SellCombinedFiatWallet
} from "@modules/cashplus/entities/cashplus.model";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { toTargetCurrency } from "@helpers/tradable-convert";

@Injectable()
export class CashplusTradingFacade {
	cashplusPortfolioFacade = inject(CashplusPortfolioFacade);
	cashPlusFacade = inject(CashPlusFacade);
	accountService = inject(AccountService);
	fiatService = inject(FiatService);
	fiatWalletService = inject(FiatWalletService);

	// cash plus and summary FF
	cashPlusBuyEnabled$ = this.cashPlusFacade.isCashPlusEnabled$();

	// check if wallet is eligible for cash plus programm
	private checkIfWalletIsEligible(fiatPid: string, configs: FiatWalletsConfigurations[]): boolean {
		if (configs) {
			return configs.some((conf) => conf.fiat_pid === fiatPid);
		}

		return false;
	}

	// Fetch configuration
	getConfig(): void {
		this.cashPlusFacade.fetchApiConfigurations();
	}

	// view model for the fiat tiles
	buyFiatTilesVM$(fiatWallets: FiatWallet[]): Observable<ICashPlusFiatWallet[]> {
		return this.cashPlusFacade.isCashPlusEnabled$().pipe(
			switchMap((state) => {
				if (state) {
					return this.checkFiatWalletWithPortfolioOverviewHelper(fiatWallets);
				}

				return EMPTY;
			})
		);
	}

	// stream for checking portfolio and overview
	checkFiatWalletWithPortfolioOverviewHelper = (fiatWallets: FiatWallet[]): Observable<ICashPlusFiatWallet[]> =>
		combineLatest([
			this.cashplusPortfolioFacade.getPortfolioState$(),
			this.cashPlusFacade.getConfigurationsState$(),
			this.cashPlusFacade.getSubscriptionsState$()
		]).pipe(
			map(([overview, config, subsciption]) =>
				// sort all wallet that isEligible first. isEligible, means it is in cash plus programm (-1 preceed 1)
				this.portfolioOverviewToFiatWalletDeserializer(
					overview,
					fiatWallets,
					config.configurations,
					subsciption
				).sort((a) => (a.isEligible ? -1 : 1))
			)
		);

	sellFiatTilesVM$(fiatWallets: FiatWallet[]): Observable<SellCombinedFiatWallet> {
		return this.cashPlusFacade.isCashPlusEnabled$().pipe(
			switchMap((state) => {
				if (state) {
					return combineLatest([
						this.cashplusPortfolioFacade.getPortfolioState$(),
						this.cashPlusFacade.getConfigurationsState$(),
						this.cashPlusFacade.getSubscriptionsState$()
					]).pipe(
						map(([overview, config, subscription]) => {
							const cashPlusWallets: ICashPlusFiatWallet[] =
								this.portfolioOverviewToFiatWalletDeserializer(
									overview,
									fiatWallets,
									config.configurations,
									subscription
								);
							const filteredFiatWallets: FiatWallet[] = fiatWallets.filter((wallet) =>
								cashPlusWallets.every(
									(cpWallet) => cpWallet.fiatWallet.getFiatId() !== wallet.getFiatId()
								)
							);
							return { cashPlusFiatWallets: cashPlusWallets, remainingFiatWallets: filteredFiatWallets };
						})
					);
				}

				return EMPTY;
			})
		);
	}

	// deserializer helper
	private portfolioOverviewToFiatWalletDeserializer(
		data: CashPlusPortfolioState,
		fiatWallets: FiatWallet[],
		config: FiatWalletsConfigurations[],
		subscription: CashPlusSubscriptionsState
	): ICashPlusFiatWallet[] {
		const defaultFiat = this.accountService.getDefaultFiat();

		if (data?.fiatConfigs.length) {
			const allWallets = fiatWallets.map((fiatWallet) => {
				// if fiat wallet is not eligible for cash plus, return it as it is (without cashplus properties)
				const currentFiatWalletPid = config.find((c) => c.fiat_id === fiatWallet.getFiatId())?.fiat_pid;
				const matchedCashPlusWallet = data.fiatConfigs.find(
					(fiatConfig) => fiatConfig.asset_pid === currentFiatWalletPid
				);
				const fiat: Fiat = this.fiatService.findById(fiatWallet.getFiatId());

				// this means the matching cashPlus wallet was found
				if (matchedCashPlusWallet) {
					return {
						id: fiat.getId(),
						pid: matchedCashPlusWallet.asset_pid,
						logo: fiat.getLogo(),
						name: fiat.getName(),
						fiatWallet,
						assetBalance: matchedCashPlusWallet.asset_balance,
						fiatBalance: toTargetCurrency(+matchedCashPlusWallet.fiat_balance, defaultFiat, fiat) + "",
						parkedAssetBalance: matchedCashPlusWallet.parked_asset_balance,
						availableAssetBalance: matchedCashPlusWallet.available_asset_balance,
						showFiatConversion: defaultFiat.getId() !== fiat.getId(),
						defaultFiat,
						isEligible: true,
						isOptIn: subscription.subscribed
					};
				}

				// handle case for cashplus wallet was not found
				return {
					id: fiat.getId(),
					pid: "",
					logo: fiat.getLogo(),
					name: fiat.getName(),
					fiatWallet,
					assetBalance: fiatWallet.getBalance() + "",
					fiatBalance: toTargetCurrency(fiatWallet.getBalance(), defaultFiat, fiat) + "",
					parkedAssetBalance: "0",
					availableAssetBalance: "0",
					showFiatConversion: defaultFiat.getId() !== fiat.getId(),
					defaultFiat,
					isEligible: false,
					isOptIn: subscription.subscribed
				};
			});

			return allWallets.filter((w) => +w.fiatBalance + +w.parkedAssetBalance > 0);
		}

		return [];
	}
}
